/**
 * Authentication Initialization Hook
 * 
 * Custom hook that initializes authentication state from localStorage
 * when the application starts. This hook is used to restore user sessions
 * and maintain authentication state across browser refreshes.
 */

import { useEffect, useRef } from 'react';
import { useAppDispatch } from './redux';
import { loginSuccess, setLoading } from '../store/authSlice';
import { authService } from '../services/authService';
import { useRBAC } from './useRBAC';

/**
 * useAuthInit Hook
 * 
 * Checks for stored authentication data in localStorage and
 * initializes the Redux auth state accordingly. This ensures
 * that users remain logged in after page refreshes.
 * 
 * Also fetches user profile with RBAC permissions from API on app load.
 */
export const useAuthInit = (enabled = true) => {
  const dispatch = useAppDispatch();
  const rbac = useRBAC();
  const { setUserProfile, setAppraisalCycle, clearUserProfile } = rbac;
  
  // Track if initialization has already been performed to prevent duplicate API calls
  const isInitialized = useRef(false);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Prevent duplicate initialization on re-renders
    if (isInitialized.current) {
      return;
    }
    isInitialized.current = true;
    /**
     * Initialize authentication state and RBAC permissions
     * 
     * 1. Checks for stored authentication
     * 2. Restores user session
     * 3. Fetches fresh RBAC profile from API
     */
    const initAuth = async () => {
      try {
        // Use authService to check if user is authenticated and get stored user
        const isAuth = authService.isAuthenticated();
        const storedUser = authService.getStoredUser();
        
        if (isAuth && storedUser) {
          console.log('🔄 Restoring user session from authService:', storedUser);
          // Restore user session in Redux store
          dispatch(loginSuccess(storedUser));
          
          // Always fetch fresh RBAC profile from API on app load
          if (storedUser.email) {
            try {
              console.log('🔐 Fetching RBAC profile from API on app load...');
              const profileResponse = await authService.getUserProfileWithPermissions(storedUser.email);
              
              if (profileResponse.status && profileResponse.data) {
                console.log('✅ RBAC profile loaded successfully:', profileResponse.data);
                setUserProfile(profileResponse.data);

                // Fetch appraisal cycle data
                console.log('📅 Fetching appraisal cycle data...');
                try {
                  const cycleResponse = await authService.getAppraisalCycle(storedUser.email);
                  
                  if (cycleResponse.status && cycleResponse.data && Object.keys(cycleResponse.data).length > 0) {
                    console.log('✅ Appraisal cycle loaded successfully:', cycleResponse.data);
                    setAppraisalCycle(cycleResponse.data);
                  } else {
                    console.warn('⚠️ Failed to load appraisal cycle:', cycleResponse.message);
                    
                    // Clear localStorage if API returned 404 or empty data
                    if (cycleResponse.status_code === 404 || (cycleResponse.data && Object.keys(cycleResponse.data).length === 0)) {
                      console.log('🧹 Clearing cached appraisal cycle due to 404 or empty response');
                      localStorage.removeItem('appraisalCycle');
                      setAppraisalCycle(null);
                    } else {
                      // Fallback: Try to load from localStorage as backup for other errors
                      const storedCycle = localStorage.getItem('appraisalCycle');
                      if (storedCycle) {
                        const cycle = JSON.parse(storedCycle);
                        console.log('📦 Using cached appraisal cycle as fallback:', cycle);
                        setAppraisalCycle(cycle);
                      } else {
                        console.warn('⚠️ No appraisal cycle available - neither from API nor cache');
                        setAppraisalCycle(null);
                      }
                    }
                  }
                } catch (cycleError) {
                  console.error('❌ Error fetching appraisal cycle:', cycleError);
                  
                  // Fallback: Try to load from localStorage
                  const storedCycle = localStorage.getItem('appraisalCycle');
                  if (storedCycle) {
                    const cycle = JSON.parse(storedCycle);
                    console.log('📦 Using cached appraisal cycle after API error:', cycle);
                    setAppraisalCycle(cycle);
                  } else {
                    console.warn('⚠️ No cached appraisal cycle available');
                  }
                }
              } else {
                console.warn('⚠️ Failed to load RBAC profile:', profileResponse.message);
                
                // Fallback: Try to load from localStorage as backup
                const storedProfile = localStorage.getItem('userProfile');
                if (storedProfile) {
                  const profile = JSON.parse(storedProfile);
                  console.log('� Using cached RBAC profile as fallback:', profile);
                  setUserProfile(profile);
                } else {
                  console.error('❌ No RBAC profile available - neither from API nor cache');
                }
              }
            } catch (profileError) {
              console.error('❌ Error fetching RBAC profile:', profileError);
              
              // Fallback: Try to load from localStorage
              try {
                const storedProfile = localStorage.getItem('userProfile');
                if (storedProfile) {
                  const profile = JSON.parse(storedProfile);
                  console.log('📦 Using cached RBAC profile after API error:', profile);
                  setUserProfile(profile);
                } else {
                  console.warn('⚠️ No cached RBAC profile available - user may have limited access');
                }
              } catch (cacheError) {
                console.error('❌ Failed to load cached profile:', cacheError);
              }
            }
          } else {
            console.warn('⚠️ No email found in stored user - cannot fetch RBAC profile');
          }
        } else if (isAuth && !storedUser) {
          // Token exists but no user data - clear invalid session
          console.warn('⚠️ Found auth token without user data, clearing session');
          authService.clearAuth();
        } else {
          // No authentication - check for legacy data and clean up
          const legacyUser = localStorage.getItem('auth_user');
          if (legacyUser) {
            console.warn('⚠️ Found legacy user data without token, clearing session');
            authService.clearAuth();
          }
          console.log('ℹ️ No authenticated user - RBAC profile not loaded');
          // Set RBAC loading to false since no user to load
          clearUserProfile();
        }
      } catch (error) {
        console.error('❌ Failed to initialize authentication:', error);
        // Clear corrupted data
        authService.clearAuth();
        clearUserProfile();
      } finally {
        // Always set loading to false after auth check
        dispatch(setLoading(false));
      }
    };

    initAuth();
  }, [enabled, dispatch, setUserProfile, setAppraisalCycle, clearUserProfile]);
};
